import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  X, ArrowLeft, Gauge, Wrench, FileCheck, Clock, AlertTriangle, CheckCircle,
  ChevronDown, ChevronUp, Truck, Container, Settings, Navigation
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_CONFIG = {
  measurement: { icon: Gauge, color: 'text-amber-400', bg: 'bg-amber-500/10', label: 'Mesure freins/pneus' },
  work_order: { icon: Wrench, color: 'text-blue-400', bg: 'bg-blue-500/10', label: 'Bon de travail' },
  inspection: { icon: FileCheck, color: 'text-emerald-400', bg: 'bg-emerald-500/10', label: 'Inspection Mécanique' },
  maintenance: { icon: Settings, color: 'text-sky-400', bg: 'bg-sky-500/10', label: 'Entretien' },
  odometer: { icon: Navigation, color: 'text-violet-300', bg: 'bg-violet-500/10', label: 'Kilométrage' },
};

const STATUS_MAP = {
  open: 'Ouvert', in_progress: 'En cours', completed: 'Complete', cancelled: 'ANNULÉ',
  ok: 'Conforme', defect: 'Defauts',
};

export default function VehicleHistoryModal({ vehicle, token, apiPrefix, onClose }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const fetchHistory = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/${apiPrefix}/${vehicle.id}/history`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setHistory(res.data || []);
      } catch { setHistory([]); }
      finally { setLoading(false); }
    };
    fetchHistory();
  }, [vehicle.id, token, apiPrefix]);

  const filtered = history.filter(h => {
    if (filter === 'all') return true;
    return h.type === filter;
  });

  const vehLabel = `${vehicle.unit_number ? `#${vehicle.unit_number} ` : ''}${vehicle.plate_number || ''}`.trim();

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div className="bg-slate-900 w-full sm:max-w-2xl sm:rounded-2xl rounded-t-2xl max-h-[90vh] overflow-hidden border border-slate-700/50 flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-slate-900 border-b border-slate-700/50 p-4 flex items-center gap-3 flex-shrink-0">
          <button onClick={onClose} className="text-slate-400 hover:text-white"><ArrowLeft size={20} /></button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              {vehicle.vehicle_type === 'trailer' ? <Container size={18} className="text-slate-400" /> : <Truck size={18} className="text-amber-400" />}
              <h2 className="text-white font-bold truncate" data-testid="history-modal-title">
                Historique - {vehLabel}
              </h2>
            </div>
            <p className="text-slate-500 text-xs mt-0.5">{vehicle.brand} {vehicle.model} {vehicle.year}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X size={20} /></button>
        </div>

        {/* Filters */}
        <div className="px-4 py-2 flex gap-1.5 border-b border-slate-800/50 flex-shrink-0">
          {[
            { key: 'all', label: 'Tout' },
            { key: 'measurement', label: 'Mesures' },
            { key: 'work_order', label: 'Bons' },
            { key: 'inspection', label: 'Inspections' },
            { key: 'maintenance', label: 'Entretiens' },
            { key: 'odometer', label: 'Kilométrage' },
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              data-testid={`history-filter-${f.key}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                filter === f.key ? 'bg-amber-500 text-black' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
              }`}
            >
              {f.label}
            </button>
          ))}
          <span className="ml-auto text-xs text-slate-500 self-center">{filtered.length} entree(s)</span>
        </div>

        {/* Timeline */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-amber-500/30 border-t-amber-500 rounded-full animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Clock size={40} className="mx-auto mb-3 opacity-30" />
              <p>Aucun historique pour ce vehicule</p>
            </div>
          ) : (
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-[19px] top-0 bottom-0 w-px bg-slate-700/50" />

              <div className="space-y-3">
                {filtered.map((item, idx) => {
                  const cfg = TYPE_CONFIG[item.type] || TYPE_CONFIG.measurement;
                  const Icon = cfg.icon;
                  const isExpanded = expandedId === item.id;

                  return (
                    <div key={item.id || idx} className="relative pl-12" data-testid={`history-item-${item.id}`}>
                      {/* Timeline dot */}
                      <div className={`absolute left-2.5 top-3 w-[14px] h-[14px] rounded-full ${cfg.bg} border-2 border-slate-900 flex items-center justify-center z-10`}>
                        <div className={`w-1.5 h-1.5 rounded-full ${item.type === 'measurement' ? 'bg-amber-500' : item.type === 'work_order' ? 'bg-blue-500' : 'bg-emerald-500'}`} />
                      </div>

                      <div className={`${cfg.bg} border border-slate-700/30 rounded-xl overflow-hidden`}>
                        <button onClick={() => setExpandedId(isExpanded ? null : item.id)} className="w-full p-3 text-left">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex items-center gap-2 min-w-0">
                              <Icon size={14} className={cfg.color} />
                              <div className="min-w-0">
                                <p className="text-white text-sm font-medium truncate">{item.title}</p>
                                <p className="text-slate-500 text-xs truncate">{item.detail}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-1.5 flex-shrink-0">
                              {item.version > 1 && <span className="text-[10px] px-1.5 rounded bg-violet-500/20 text-violet-300" data-testid={`history-version-${item.id}`}>v{item.version}</span>}
                              <span className="text-[10px] text-slate-500">{item.date}</span>
                              {isExpanded ? <ChevronUp size={14} className="text-slate-500" /> : <ChevronDown size={14} className="text-slate-500" />}
                            </div>
                          </div>
                        </button>

                        {isExpanded && (
                          <div className="px-3 pb-3 pt-0 border-t border-slate-700/30 space-y-2">
                            <div className="grid grid-cols-2 gap-2 text-xs mt-2">
                              {item.by && (
                                <div>
                                  <span className="text-slate-500">Par: </span>
                                  <span className="text-slate-300">{item.by}</span>
                                  {item.source === 'atelier' && <span className="text-amber-400/60 ml-1">(Atelier)</span>}
                                </div>
                              )}
                              {item.odometer > 0 && (
                                <div>
                                  <span className="text-slate-500">Odometre: </span>
                                  <span className="text-slate-300">{item.odometer.toLocaleString()} km</span>
                                </div>
                              )}
                              {item.status && (
                                <div>
                                  <span className="text-slate-500">Statut: </span>
                                  <span className={`font-medium ${
                                    item.status === 'completed' || item.status === 'ok' ? 'text-emerald-400' :
                                    item.status === 'defect' || item.status === 'cancelled' ? 'text-red-400' : 'text-amber-400'
                                  }`}>
                                    {STATUS_MAP[item.status] || item.status}
                                  </span>
                                </div>
                              )}
                            </div>

                            {/* Measurement detail */}
                            {item.type === 'measurement' && item.axles && (
                              <div className="space-y-1.5 mt-1">
                                {item.axles.map((axle, ai) => (
                                  <div key={ai} className="bg-slate-800/50 rounded-lg p-2 text-xs">
                                    <p className="text-amber-400/80 text-[10px] font-medium mb-1">Essieu {axle.axle_number || ai + 1}</p>
                                    <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
                                      <span className="text-slate-500">Frein G: <span className="text-white">{axle.left_brake ?? '-'}</span></span>
                                      <span className="text-slate-500">Frein D: <span className="text-white">{axle.right_brake ?? '-'}</span></span>
                                      <span className="text-slate-500">Pneu G int: <span className="text-white">{axle.left_tire_inner ?? '-'}</span></span>
                                      <span className="text-slate-500">Pneu D int: <span className="text-white">{axle.right_tire_inner ?? '-'}</span></span>
                                      {axle.has_dual_tires && (
                                        <>
                                          <span className="text-slate-500">Pneu G ext: <span className="text-white">{axle.left_tire_outer ?? '-'}</span></span>
                                          <span className="text-slate-500">Pneu D ext: <span className="text-white">{axle.right_tire_outer ?? '-'}</span></span>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
